import { t } from "i18next"
import {
  Button,
  DetailBar,
  Group,
  InputNumber,
  Modal,
  PriceSlider,
  RadioButtons,
  SelectInput,
  TextInput,
  Title,
} from "components/common"
import { useAddAnswersMutation, useLazyGetClientAnswerQuery, useLazyGetClientQuestionsQuery } from "features/tasks/api"
import { useEffect, useMemo } from "react"
import { useLazyGetCitiesQuery, useLazyGetCountriesQuery, useLazyGetLocalityQuery } from "features/shared/api"
import { Form } from "antd"
import getClientProfileInitialValues from "./clientProfileInitialValues"
import { localisedValue, convertToSpecificAreaUnit, requiredValidator } from "utils"
import { useSelector } from "react-redux"
import { useWatch } from "antd/lib/form/Form"
import ClientProfileFormSkeleton from "./clientProfileFormSkeleton"

export default function ClientProfileForm({ formRef, client, allocatedTo, onSuccess = () => {}, onClose = () => {} }) {
  const [form] = Form.useForm()
  const { setFieldsValue, setFieldValue } = form
  const selectedCityIds = useWatch("preferredCityIds", form)
  const occupationWatch = useWatch("occupation", form)
  const netWorthValue = useWatch("netWorth", form)
  const preferredCountryId = useWatch("preferredCountryId", form)
  const annualRevenueAUMValue = useWatch("annualRevenueAUM", form)
  const budgetValue = useWatch("budget", form)
  const capitalStructure = useWatch("capitalStructure", form)
  const targetAllocationValue = useWatch("targetAllocation", form)
  const leverageRatioValue = useWatch("leverageRatio", form)
  const [getQuestions, { data: questionData, isFetching }] = useLazyGetClientQuestionsQuery()
  const [getClientAnswers, { data: answerData }] = useLazyGetClientAnswerQuery()
  const [getCities, { data: cities }] = useLazyGetCitiesQuery()
  const [getLocalities, { data: localities }] = useLazyGetLocalityQuery()
  const [getCountries, { data: countries }] = useLazyGetCountriesQuery()
  const [addAnswers, { isError, isLoading, reset, error }] = useAddAnswersMutation()

  const getClientDetail = useMemo(
    () => [
      { label: t("Client"), value: client?.name },
      { label: t("Allocated To"), value: client?.allocationActivity?.name || client?.User?.name || allocatedTo },
    ],
    [client, allocatedTo]
  )
  const getNationalities = useMemo(() => {
    return countries?.map(country => ({
      label: localisedValue({ object: country, key: "nationalityTitle" }),
      value: country.id,
    }))
  }, [countries])

  const getOptionsFromData = (fieldData, useValue = false) => {
    const options = fieldData?.settings?.options || fieldData?.options
    return options?.map(option => ({
      label:
        localisedValue({ object: option, key: "displayTitle" }) ||
        localisedValue({ object: option, key: "name" }) ||
        option?.displayTitle ||
        option?.name,
      value: useValue ? option?.value || option?.key : option?.id || option?.value || option?.key,
    }))
  }
  const getFieldRules = fieldData => {
    if (!fieldData?.required) return []
    const fieldName = localisedValue({ object: fieldData, key: "title" }) || ""
    return [requiredValidator(fieldName)]
  }

  const MAX_SLIDER_VALUE = 5000000000

  const renderQuickChips = (fieldData, fieldName) => {
    const quickChipOptions = fieldData?.settings?.quickChipOptions
    if (!quickChipOptions?.length) return null

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
        {quickChipOptions?.map(option => {
          const isOpenEnded = option?.label?.includes("+")
          const minValue = +(option?.minValue ?? 0)
          const maxValue = isOpenEnded
            ? MAX_SLIDER_VALUE
            : Math.min(+(option?.maxValue ?? MAX_SLIDER_VALUE), MAX_SLIDER_VALUE)
          return (
            <Button
              key={option?.label}
              size="small"
              type="primary-light"
              style={{ borderRadius: 20, padding: "2px 10px", fontSize: 12, height: "auto", lineHeight: "20px" }}
              onClick={() => setFieldValue(fieldName, [minValue, maxValue])}
            >
              {option?.label}
            </Button>
          )
        })}
      </div>
    )
  }

  const getSliderRange = (fieldData, useOptions = false) => {
    const min = useOptions ? +(fieldData?.options?.[0]?.minValue || 0) : +(fieldData?.settings?.minValue || 0)
    const max = useOptions ? +(fieldData?.options?.[0]?.maxValue || 0) : +(fieldData?.settings?.maxValue || 0)
    return [min, fieldData?.settings?.key === "leverageRatio" ? max : Math.max(max, MAX_SLIDER_VALUE)]
  }

  const renderSliderField = (
    fieldData,
    fieldName,
    watchedValue,
    { showChips = true, useOptions = false, minFixedAtZero = false } = {}
  ) => {
    if (!fieldData) return null
    return (
      <Form.Item
        name={fieldName}
        label={localisedValue({ object: fieldData, key: "title" })}
        required={fieldData?.required}
        rules={getFieldRules(fieldData)}
      >
        <>
          <PriceSlider
            name={fieldName}
            value={watchedValue}
            range={getSliderRange(fieldData, useOptions)}
            steps={fieldData?.settings?.key === "leverageRatio" ? 1 : 100000}
            setField={setFieldValue}
            sliderMaxWidth={400}
            {...(minFixedAtZero && { minFixedAtZero: true })}
          />
          {showChips && renderQuickChips(fieldData, fieldName)}
        </>
      </Form.Item>
    )
  }

  const onModalVisible = () => {
    getClientAnswers(client?.id)
    getQuestions(client?.id)
    getCountries()
  }
  const onSubmit = async values => {
    reset()
    addAnswers({ values, client })
      .unwrap()
      .then(() => {
        if (!isError) {
          onSuccess()
          formRef?.current?.setVisible(false)
        }
      })
  }

  useEffect(() => {
    answerData && setFieldsValue(getClientProfileInitialValues(answerData))
    if (answerData?.preferredCountryId) {
      getCities(answerData?.preferredCountryId)
    }
    if (answerData?.preferredCityIds?.length == 1) {
      getLocalities(answerData?.preferredCityIds[0])
    }
  }, [answerData])

  // checking if section has any visible fields
  const hasPersonalInfoFields =
    questionData?.relationshipStrength ||
    questionData?.gender ||
    questionData?.nationalityId ||
    questionData?.maritalStatus ||
    questionData?.children

  const hasFinancialFields =
    questionData?.companyNameProfiling ||
    questionData?.industryId ||
    questionData?.occupation ||
    questionData?.teamSize ||
    questionData?.netWorth ||
    questionData?.annualRevenueAUM ||
    questionData?.clientRatingId ||
    questionData?.keyShareholderUBO

  const hasPurchaseCriteriaFields =
    questionData?.budget ||
    questionData?.interestedTypeId ||
    questionData?.area ||
    questionData?.preferredCountryId ||
    questionData?.preferredCityIds ||
    questionData?.preferredCityRegion ||
    questionData?.hasExistingProperty ||
    questionData?.preferredLocalityId ||
    questionData?.intent ||
    questionData?.buyingIntentId ||
    questionData?.preferredFinancing

  const hasMarketExposureFields =
    questionData?.politicalExposure ||
    questionData?.realEstateIndustryExposure ||
    questionData?.marketExposure ||
    questionData?.socialCapital

  const hasInvestmentStrategyFields =
    questionData?.investmentStrategy ||
    questionData?.investmentPeriod ||
    questionData?.targetAnnualIncomePercent ||
    questionData?.targetCapitalReturnPercent ||
    questionData?.targetAllocation ||
    questionData?.capitalStructure ||
    questionData?.leverageRatio ||
    questionData?.assetClass ||
    questionData?.riskProfile ||
    questionData?.currentInvestmentPortfolio

  const hasServicesFields =
    questionData?.residencyRequirement ||
    questionData?.propertyManagement ||
    questionData?.legalTaxServices ||
    questionData?.travelLogisticsServices ||
    questionData?.shortTermLease ||
    questionData?.longTermLease ||
    questionData?.resaleFlipping

  return (
    <Modal
      title={t("Fill Client Profile")}
      ref={formRef}
      okText={t("Save")}
      width={1000}
      cancelText={t("Cancel")}
      onModalVisible={onModalVisible}
      onCancel={() => {
        form.resetFields()
        formRef?.current?.setVisible(false)
        reset()
        onClose()
      }}
      onOk={form.submit}
      loading={isLoading}
      destroyOnClose
      footerAlert={error}
    >
      <>
        {isFetching ? (
          <ClientProfileFormSkeleton />
        ) : (
          <>
            <div className="mbe-24">
              <Title level={3}>{t("Client Profile")}</Title>
              <DetailBar detailData={getClientDetail} />
            </div>

            <Form
              form={form}
              layout="vertical"
              name="client-Profile"
              initialValues={getClientProfileInitialValues(answerData)}
              onFinish={onSubmit}
              preserve={false}
              disabled={isFetching}
              scrollToFirstError
              colon={false}
            >
              {/* ============ PERSONAL INFORMATION ============ */}
              {hasPersonalInfoFields && (
                <>
                  <div className="mbe-24">
                    <Title level={4}>{t("Personal Information")}</Title>
                  </div>
                  <Group template="repeat(2, 1fr)" gap="16px 64px" className="mbe-24">
                    {questionData?.relationshipStrength && (
                      <Form.Item
                        name="relationshipStrength"
                        label={localisedValue({ object: questionData?.relationshipStrength, key: "title" })}
                        required={questionData?.relationshipStrength?.required}
                        rules={getFieldRules(questionData?.relationshipStrength)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.relationshipStrength)} />
                      </Form.Item>
                    )}
                    {questionData?.gender && (
                      <Form.Item
                        name="gender"
                        label={localisedValue({ object: questionData?.gender, key: "title" })}
                        required={questionData?.gender?.required}
                        rules={getFieldRules(questionData?.gender)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.gender)} />
                      </Form.Item>
                    )}
                    {questionData?.nationalityId && (
                      <Form.Item
                        name="nationalityId"
                        label={localisedValue({ object: questionData?.nationalityId, key: "title" })}
                        required={questionData?.nationalityId?.required}
                        rules={getFieldRules(questionData?.nationalityId)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={localisedValue({ object: questionData?.nationalityId, key: "placeholder" })}
                          options={getNationalities}
                          onChange={value => setFieldValue("nationalityId", value)}
                        />
                      </Form.Item>
                    )}
                    {questionData?.maritalStatus && (
                      <Form.Item
                        name="maritalStatus"
                        label={localisedValue({ object: questionData?.maritalStatus, key: "title" })}
                        required={questionData?.maritalStatus?.required}
                        rules={getFieldRules(questionData?.maritalStatus)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.maritalStatus)} />
                      </Form.Item>
                    )}
                    {questionData?.children && (
                      <Form.Item
                        name="children"
                        label={localisedValue({ object: questionData?.children, key: "title" })}
                        required={questionData?.children?.required}
                        rules={getFieldRules(questionData?.children)}
                      >
                        <RadioButtons
                          options={[
                            { label: t("Yes"), value: "true" },
                            { label: t("No"), value: "false" },
                          ]}
                        />
                      </Form.Item>
                    )}

                    {questionData?.taxResidency && (
                      <Form.Item
                        name="taxResidency"
                        label={localisedValue({ object: questionData?.taxResidency, key: "title" })}
                        required={questionData?.taxResidency?.required}
                        rules={getFieldRules(questionData?.taxResidency)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={localisedValue({ object: questionData?.taxResidency, key: "placeholder" })}
                          options={countries}
                        />
                      </Form.Item>
                    )}
                  </Group>
                </>
              )}

              {/* ============ FINANCIAL & PROFESSIONAL DETAILS ============ */}
              {hasFinancialFields && (
                <>
                  <div className="mbe-24">
                    <Title level={4}>{t("Financial & Professional Details")}</Title>
                  </div>
                  <Group template="repeat(2, 1fr)" gap="16px 64px" className="mbe-24">
                    {questionData?.companyNameProfiling && (
                      <Form.Item
                        name="companyNameProfiling"
                        label={localisedValue({ object: questionData?.companyNameProfiling, key: "title" })}
                        required={questionData?.companyNameProfiling?.required}
                        rules={getFieldRules(questionData?.companyNameProfiling)}
                      >
                        <TextInput
                          placeholder={localisedValue({
                            object: questionData?.companyNameProfiling,
                            key: "placeholder",
                          })}
                        />
                      </Form.Item>
                    )}
                    {questionData?.industryId && (
                      <Form.Item
                        name="industryId"
                        label={localisedValue({ object: questionData?.industryId, key: "title" })}
                        required={questionData?.industryId?.required}
                        rules={getFieldRules(questionData?.industryId)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={localisedValue({ object: questionData?.industryId, key: "placeholder" })}
                          options={getOptionsFromData(questionData?.industryId)}
                          onChange={value => setFieldValue("industryId", value)}
                        />
                      </Form.Item>
                    )}
                    {questionData?.occupation && (
                      <Form.Item
                        name="occupation"
                        label={localisedValue({ object: questionData?.occupation, key: "title" })}
                        required={questionData?.occupation?.required}
                        rules={getFieldRules(questionData?.occupation)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={localisedValue({ object: questionData?.occupation, key: "placeholder" })}
                          options={getOptionsFromData(questionData?.occupation, true)}
                          onChange={value => setFieldsValue({ occupation: value, occupationOtherText: null })}
                        />
                      </Form.Item>
                    )}

                    {questionData?.occupation && occupationWatch === "others" && (
                      <Form.Item
                        name="occupationOtherText"
                        label={t("Occupation (Other)")}
                        required
                        rules={[requiredValidator(t("Occupation (Other)"))]}
                      >
                        <TextInput placeholder={t("Specify Occupation")} maxLength={255} showCount />
                      </Form.Item>
                    )}
                    {questionData?.teamSize && (
                      <Form.Item
                        name="teamSize"
                        label={localisedValue({ object: questionData?.teamSize, key: "title" })}
                        required={questionData?.teamSize?.required}
                        rules={getFieldRules(questionData?.teamSize)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={
                            localisedValue({ object: questionData?.teamSize, key: "placeholder" }) || t("Select")
                          }
                          options={getOptionsFromData(questionData?.teamSize, true)}
                          onChange={value => setFieldValue("teamSize", value)}
                        />
                      </Form.Item>
                    )}
                    {renderSliderField(questionData?.netWorth, "netWorth", netWorthValue)}
                    {renderSliderField(questionData?.annualRevenueAUM, "annualRevenueAUM", annualRevenueAUMValue)}
                    {questionData?.clientRatingId && (
                      <Form.Item
                        name="clientRatingId"
                        label={localisedValue({ object: questionData?.clientRatingId, key: "title" })}
                        required={questionData?.clientRatingId?.required}
                        rules={getFieldRules(questionData?.clientRatingId)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={localisedValue({ object: questionData?.clientRatingId, key: "placeholder" })}
                          options={getOptionsFromData(questionData?.clientRatingId)}
                          onChange={value => setFieldValue("clientRatingId", value)}
                        />
                      </Form.Item>
                    )}
                    {questionData?.keyShareholderUBO && (
                      <Form.Item
                        name="keyShareholderUBO"
                        label={localisedValue({ object: questionData?.keyShareholderUBO, key: "title" })}
                        required={questionData?.keyShareholderUBO?.required}
                        rules={getFieldRules(questionData?.keyShareholderUBO)}
                      >
                        <TextInput
                          placeholder={localisedValue({ object: questionData?.keyShareholderUBO, key: "placeholder" })}
                        />
                      </Form.Item>
                    )}
                  </Group>
                </>
              )}

              {/* ============ PURCHASE CRITERIA ============ */}
              {hasPurchaseCriteriaFields && (
                <>
                  <div className="mbe-24">
                    <Title level={4}>{t("Purchase Criteria")}</Title>
                  </div>
                  <Group template="repeat(2, 1fr)" gap="16px 64px" className="mbe-24">
                    {questionData?.interestedTypeId && (
                      <Form.Item
                        name="interestedTypeId"
                        label={localisedValue({ object: questionData?.interestedTypeId, key: "title" })}
                        required={questionData?.interestedTypeId?.required}
                        rules={getFieldRules(questionData?.interestedTypeId)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={localisedValue({ object: questionData?.interestedTypeId, key: "placeholder" })}
                          options={getOptionsFromData(questionData?.interestedTypeId)}
                          onChange={value => setFieldValue("interestedTypeId", value)}
                        />
                      </Form.Item>
                    )}
                    {questionData?.area && (
                      <Form.Item
                        name="area"
                        label={localisedValue({ object: questionData?.area, key: "title" })}
                        required={questionData?.area?.required}
                        rules={getFieldRules(questionData?.area)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={localisedValue({ object: questionData?.area, key: "placeholder" })}
                          options={getOptionsFromData(questionData?.area)}
                          onChange={value => setFieldValue("area", value)}
                        />
                      </Form.Item>
                    )}
                    {questionData?.preferredCountryId && (
                      <Form.Item
                        name="preferredCountryId"
                        label={localisedValue({ object: questionData?.preferredCountryId, key: "title" })}
                        required={questionData?.preferredCountryId?.required}
                        rules={getFieldRules(questionData?.preferredCountryId)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={localisedValue({ object: questionData?.preferredCountryId, key: "placeholder" })}
                          options={countries}
                          onChange={value => {
                            setFieldsValue({
                              preferredCountryId: value,
                              preferredCityIds: null,
                              preferredCityRegion: null,
                              preferredLocalityId: null,
                            })
                            getCities(value)
                          }}
                        />
                      </Form.Item>
                    )}
                    {questionData?.preferredCityIds && preferredCountryId && (
                      <Form.Item
                        name="preferredCityIds"
                        label={localisedValue({ object: questionData?.preferredCityIds, key: "title" })}
                        required={questionData?.preferredCityIds?.required}
                        rules={getFieldRules(questionData?.preferredCityIds)}
                      >
                        <SelectInput
                          showSearch
                          mode="multiple"
                          placeholder={localisedValue({ object: questionData?.preferredCityIds, key: "placeholder" })}
                          options={cities}
                          onChange={value => {
                            setFieldValue("preferredCityIds", value?.length ? value : null)
                            setFieldsValue({ preferredCityRegion: null, preferredLocalityId: null })
                            if (value?.length === 1) {
                              getLocalities(value[0])
                            }
                          }}
                        />
                      </Form.Item>
                    )}

                    {/* Show region when 0 or 1 city, hide when multiple cities */}
                    {questionData?.preferredCityRegion && (!selectedCityIds || selectedCityIds?.length === 1) && (
                      <Form.Item
                        name="preferredCityRegion"
                        label={localisedValue({ object: questionData?.preferredCityRegion, key: "title" })}
                        required={questionData?.preferredCityRegion?.required}
                        rules={getFieldRules(questionData?.preferredCityRegion)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.preferredCityRegion)} />
                      </Form.Item>
                    )}
                    {/* Show locality when 0 or 1 city, hide when multiple cities */}
                    {questionData?.preferredLocalityId &&
                      (!selectedCityIds || selectedCityIds?.length === 1) &&
                      localities?.length !== 0 && (
                        <Form.Item
                          name="preferredLocalityId"
                          label={localisedValue({ object: questionData?.preferredLocalityId, key: "title" })}
                          required={questionData?.preferredLocalityId?.required}
                          rules={getFieldRules(questionData?.preferredLocalityId)}
                        >
                          <SelectInput
                            showSearch
                            placeholder={localisedValue({
                              object: questionData?.preferredLocalityId,
                              key: "placeholder",
                            })}
                            options={localities}
                            onChange={value => setFieldValue("preferredLocalityId", value)}
                            allowClear
                          />
                        </Form.Item>
                      )}

                    {questionData?.hasExistingProperty && (
                      <Form.Item
                        name="hasExistingProperty"
                        label={localisedValue({ object: questionData?.hasExistingProperty, key: "title" })}
                        required={questionData?.hasExistingProperty?.required}
                        rules={getFieldRules(questionData?.hasExistingProperty)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.hasExistingProperty)} />
                      </Form.Item>
                    )}

                    {questionData?.intent && (
                      <Form.Item
                        name="intent"
                        label={localisedValue({ object: questionData?.intent, key: "title" })}
                        required={questionData?.intent?.required}
                        rules={getFieldRules(questionData?.intent)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={localisedValue({ object: questionData?.intent, key: "placeholder" })}
                          options={getOptionsFromData(questionData?.intent)}
                          onChange={value => setFieldValue("intent", value)}
                        />
                      </Form.Item>
                    )}
                    {questionData?.buyingIntentId && (
                      <Form.Item
                        name="buyingIntentId"
                        label={localisedValue({ object: questionData?.buyingIntentId, key: "title" })}
                        required={questionData?.buyingIntentId?.required}
                        rules={getFieldRules(questionData?.buyingIntentId)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={localisedValue({ object: questionData?.buyingIntentId, key: "placeholder" })}
                          options={getOptionsFromData(questionData?.buyingIntentId)}
                          onChange={value => setFieldValue("buyingIntentId", value)}
                        />
                      </Form.Item>
                    )}
                    {questionData?.preferredFinancing && (
                      <Form.Item
                        name="preferredFinancing"
                        label={localisedValue({ object: questionData?.preferredFinancing, key: "title" })}
                        required={questionData?.preferredFinancing?.required}
                        rules={getFieldRules(questionData?.preferredFinancing)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={localisedValue({ object: questionData?.preferredFinancing, key: "placeholder" })}
                          options={getOptionsFromData(questionData?.preferredFinancing)}
                          onChange={value => setFieldValue("preferredFinancing", value)}
                        />
                      </Form.Item>
                    )}
                  </Group>
                  {renderSliderField(questionData?.budget, "budget", budgetValue, {
                    showChips: false,
                    useOptions: true,
                    minFixedAtZero: true,
                  })}
                </>
              )}

              {/* ============ MARKET EXPOSURE ============ */}
              {hasMarketExposureFields && (
                <>
                  <div className="mbe-24">
                    <Title level={4}>{t("Market Exposure")}</Title>
                  </div>
                  <Group template="repeat(2, 1fr)" gap="16px 64px" className="mbe-24">
                    {questionData?.politicalExposure && (
                      <Form.Item
                        name="politicalExposure"
                        label={localisedValue({ object: questionData?.politicalExposure, key: "title" })}
                        required={questionData?.politicalExposure?.required}
                        rules={getFieldRules(questionData?.politicalExposure)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.politicalExposure)} />
                      </Form.Item>
                    )}
                    {questionData?.realEstateIndustryExposure && (
                      <Form.Item
                        name="realEstateIndustryExposure"
                        label={localisedValue({ object: questionData?.realEstateIndustryExposure, key: "title" })}
                        required={questionData?.realEstateIndustryExposure?.required}
                        rules={getFieldRules(questionData?.realEstateIndustryExposure)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.realEstateIndustryExposure)} />
                      </Form.Item>
                    )}
                    {questionData?.marketExposure && (
                      <Form.Item
                        name="marketExposure"
                        label={localisedValue({ object: questionData?.marketExposure, key: "title" })}
                        required={questionData?.marketExposure?.required}
                        rules={getFieldRules(questionData?.marketExposure)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.marketExposure)} />
                      </Form.Item>
                    )}
                    {questionData?.socialCapital && (
                      <Form.Item
                        name="socialCapital"
                        label={localisedValue({ object: questionData?.socialCapital, key: "title" })}
                        required={questionData?.socialCapital?.required}
                        rules={getFieldRules(questionData?.socialCapital)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={
                            localisedValue({ object: questionData?.socialCapital, key: "placeholder" }) || t("Select")
                          }
                          options={getOptionsFromData(questionData?.socialCapital, true)}
                          onChange={value => setFieldValue("socialCapital", value)}
                        />
                      </Form.Item>
                    )}
                  </Group>
                </>
              )}

              {/* ============ INVESTMENT STRATEGY ============ */}
              {hasInvestmentStrategyFields && (
                <>
                  <div className="mbe-24">
                    <Title level={4}>{t("Investment Strategy")}</Title>
                  </div>
                  <Group template="repeat(2, 1fr)" gap="16px 64px" className="mbe-24">
                    {questionData?.investmentStrategy && (
                      <Form.Item
                        name="investmentStrategy"
                        label={localisedValue({ object: questionData?.investmentStrategy, key: "title" })}
                        required={questionData?.investmentStrategy?.required}
                        rules={getFieldRules(questionData?.investmentStrategy)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={localisedValue({ object: questionData?.investmentStrategy, key: "placeholder" })}
                          options={getOptionsFromData(questionData?.investmentStrategy)}
                          onChange={value => setFieldValue("investmentStrategy", value)}
                          mode="multiple"
                        />
                      </Form.Item>
                    )}
                    {questionData?.investmentPeriod && (
                      <Form.Item
                        name="investmentPeriod"
                        label={localisedValue({ object: questionData?.investmentPeriod, key: "title" })}
                        required={questionData?.investmentPeriod?.required}
                        rules={getFieldRules(questionData?.investmentPeriod)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.investmentPeriod)} />
                      </Form.Item>
                    )}
                    {questionData?.targetAnnualIncomePercent && (
                      <Form.Item
                        name="targetAnnualIncomePercent"
                        label={localisedValue({ object: questionData?.targetAnnualIncomePercent, key: "title" })}
                        required={questionData?.targetAnnualIncomePercent?.required}
                        rules={getFieldRules(questionData?.targetAnnualIncomePercent)}
                      >
                        <InputNumber
                          addonAfter="%"
                          max={100}
                          min={0}
                          controls={false}
                          className="w-100"
                          precision={0}
                          placeholder={localisedValue({
                            object: questionData?.targetAnnualIncomePercent,
                            key: "placeholder",
                          })}
                        />
                      </Form.Item>
                    )}
                    {questionData?.targetCapitalReturnPercent && (
                      <Form.Item
                        name="targetCapitalReturnPercent"
                        label={localisedValue({ object: questionData?.targetCapitalReturnPercent, key: "title" })}
                        required={questionData?.targetCapitalReturnPercent?.required}
                        rules={getFieldRules(questionData?.targetCapitalReturnPercent)}
                      >
                        <InputNumber
                          addonAfter="%"
                          max={100}
                          min={0}
                          controls={false}
                          className="w-100"
                          precision={0}
                          placeholder={localisedValue({
                            object: questionData?.targetCapitalReturnPercent,
                            key: "placeholder",
                          })}
                        />
                      </Form.Item>
                    )}
                    {renderSliderField(questionData?.targetAllocation, "targetAllocation", targetAllocationValue)}
                    {questionData?.capitalStructure && (
                      <Form.Item
                        name="capitalStructure"
                        label={localisedValue({ object: questionData?.capitalStructure, key: "title" })}
                        required={questionData?.capitalStructure?.required}
                        rules={getFieldRules(questionData?.capitalStructure)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.capitalStructure)} />
                      </Form.Item>
                    )}
                    {capitalStructure &&
                      capitalStructure !== "un_leveraged" &&
                      renderSliderField(questionData?.leverageRatio, "leverageRatio", leverageRatioValue)}
                    {questionData?.assetClass && (
                      <Form.Item
                        name="assetClass"
                        label={localisedValue({ object: questionData?.assetClass, key: "title" })}
                        required={questionData?.assetClass?.required}
                        rules={getFieldRules(questionData?.assetClass)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={localisedValue({ object: questionData?.assetClass, key: "placeholder" })}
                          options={getOptionsFromData(questionData?.assetClass)}
                          onChange={value => setFieldValue("assetClass", value)}
                          mode="multiple"
                        />
                      </Form.Item>
                    )}
                    {questionData?.riskProfile && (
                      <Form.Item
                        name="riskProfile"
                        label={localisedValue({ object: questionData?.riskProfile, key: "title" })}
                        required={questionData?.riskProfile?.required}
                        rules={getFieldRules(questionData?.riskProfile)}
                      >
                        <SelectInput
                          showSearch
                          placeholder={localisedValue({ object: questionData?.riskProfile, key: "placeholder" })}
                          options={getOptionsFromData(questionData?.riskProfile)}
                          onChange={value => setFieldValue("riskProfile", value)}
                          mode="multiple"
                        />
                      </Form.Item>
                    )}
                    {questionData?.currentInvestmentPortfolio && (
                      <Form.Item
                        name="currentInvestmentPortfolio"
                        label={localisedValue({ object: questionData?.currentInvestmentPortfolio, key: "title" })}
                        required={questionData?.currentInvestmentPortfolio?.required}
                        rules={getFieldRules(questionData?.currentInvestmentPortfolio)}
                      >
                        <SelectInput
                          mode="multiple"
                          showSearch
                          placeholder={
                            localisedValue({ object: questionData?.currentInvestmentPortfolio, key: "placeholder" }) ||
                            t("Select")
                          }
                          options={getOptionsFromData(questionData?.currentInvestmentPortfolio, true)}
                          onChange={value => setFieldValue("currentInvestmentPortfolio", value)}
                        />
                      </Form.Item>
                    )}
                  </Group>
                </>
              )}

              {/* ============ SERVICES REQUIRED ============ */}
              {hasServicesFields && (
                <>
                  <div className="mbe-24">
                    <Title level={4}>{t("Services Required")}</Title>
                  </div>
                  <Group template="repeat(2, 1fr)" gap="16px 64px" className="mbe-24">
                    {questionData?.residencyRequirement && (
                      <Form.Item
                        name="residencyRequirement"
                        label={localisedValue({ object: questionData?.residencyRequirement, key: "title" })}
                        required={questionData?.residencyRequirement?.required}
                        rules={getFieldRules(questionData?.residencyRequirement)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.residencyRequirement)} />
                      </Form.Item>
                    )}
                    {questionData?.propertyManagement && (
                      <Form.Item
                        name="propertyManagement"
                        label={localisedValue({ object: questionData?.propertyManagement, key: "title" })}
                        required={questionData?.propertyManagement?.required}
                        rules={getFieldRules(questionData?.propertyManagement)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.propertyManagement)} />
                      </Form.Item>
                    )}
                    {questionData?.shortTermLease && (
                      <Form.Item
                        name="shortTermLease"
                        label={localisedValue({ object: questionData?.shortTermLease, key: "title" })}
                        required={questionData?.shortTermLease?.required}
                        rules={getFieldRules(questionData?.shortTermLease)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.shortTermLease)} />
                      </Form.Item>
                    )}
                    {questionData?.longTermLease && (
                      <Form.Item
                        name="longTermLease"
                        label={localisedValue({ object: questionData?.longTermLease, key: "title" })}
                        required={questionData?.longTermLease?.required}
                        rules={getFieldRules(questionData?.longTermLease)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.longTermLease)} />
                      </Form.Item>
                    )}
                    {questionData?.resaleFlipping && (
                      <Form.Item
                        name="resaleFlipping"
                        label={localisedValue({ object: questionData?.resaleFlipping, key: "title" })}
                        required={questionData?.resaleFlipping?.required}
                        rules={getFieldRules(questionData?.resaleFlipping)}
                      >
                        <RadioButtons options={getOptionsFromData(questionData?.resaleFlipping)} />
                      </Form.Item>
                    )}
                    {questionData?.legalTaxServices && (
                      <Form.Item
                        name="legalTaxServices"
                        label={localisedValue({ object: questionData?.legalTaxServices, key: "title" })}
                        required={questionData?.legalTaxServices?.required}
                        rules={getFieldRules(questionData?.legalTaxServices)}
                      >
                        <SelectInput
                          mode="multiple"
                          placeholder={localisedValue({ object: questionData?.legalTaxServices, key: "placeholder" })}
                          options={getOptionsFromData(questionData?.legalTaxServices)}
                          onChange={value => setFieldValue("legalTaxServices", value)}
                        />
                      </Form.Item>
                    )}
                    {questionData?.travelLogisticsServices && (
                      <Form.Item
                        name="travelLogisticsServices"
                        label={localisedValue({ object: questionData?.travelLogisticsServices, key: "title" })}
                        required={questionData?.travelLogisticsServices?.required}
                        rules={getFieldRules(questionData?.travelLogisticsServices)}
                      >
                        <SelectInput
                          mode="multiple"
                          placeholder={localisedValue({
                            object: questionData?.travelLogisticsServices,
                            key: "placeholder",
                          })}
                          options={getOptionsFromData(questionData?.travelLogisticsServices)}
                          onChange={value => setFieldValue("travelLogisticsServices", value)}
                        />
                      </Form.Item>
                    )}
                  </Group>
                </>
              )}
            </Form>
          </>
        )}
      </>
    </Modal>
  )
}
